// Admin users (MUI, /admin/users). Presentation port of dashboard/AdminUsersPage —
// same data/mutations (listAdminProfiles, invites, etc.), rebuilt with mui/* + brainTokens.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useProfile } from "../../lib/profile";
import {
  listAdminProfiles,
  updateAdminProfile,
  setProfileActive,
  listInvites,
  createInvite,
  revokeInvite,
  listDepartmentsApi,
  type AdminProfile,
  type Invite,
} from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, SectionCard, StatusPill, EmptyState, Icon } from "../../mui";

const c = brainTokens.color;

function ScopePillMui({ scope }: { scope: string }) {
  const s = scope === "private" ? "personal" : scope === "shared" ? "company" : scope;
  const icon =
    s === "company" ? "grid" : s.startsWith("dept:") ? "layers" : s.startsWith("personal") ? "users" : undefined;
  const label = s.startsWith("dept:") ? s.slice(5) : s;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        fontSize: 12,
        fontWeight: 600,
        borderRadius: `${brainTokens.radius.pill}px`,
        px: "10px",
        py: "4px",
        bgcolor: s === "company" ? c.tealTint : s.startsWith("dept:") ? c.panelBg2 : c.panelBg,
        color: s === "personal" ? c.muted : c.ink3,
        border: `1px solid ${c.borderLight}`,
      }}
    >
      {icon && <Icon name={icon} size={13} stroke={1.8} />}
      {label}
    </Box>
  );
}

function SectionTitle({ icon, children }: { icon: string; children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: 15, fontWeight: 500, color: c.ink }}>
      <Icon name={icon} size={17} stroke={1.8} />
      {children}
    </Box>
  );
}

function DeptChecks({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [options, setOptions] = useState<string[]>(["dev", "devops", "product", "support", "sales", "finance"]);
  useEffect(() => {
    void listDepartmentsApi()
      .then((rows) => setOptions(rows.map((r) => r.deptId)))
      .catch(() => undefined);
  }, []);
  const toggle = (d: string) => onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d]);
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {options.map((d) => (
        <FormControlLabel
          key={d}
          sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12 } }}
          control={<Checkbox size="small" checked={value.includes(d)} onChange={() => toggle(d)} />}
          label={d}
        />
      ))}
    </Box>
  );
}

function UserRow({ p, isSelf, onChanged }: { p: AdminProfile; isSelf: boolean; onChanged: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [depts, setDepts] = useState<string[]>(p.departments);
  const [role, setRole] = useState(p.role === "admin" ? "admin" : "member");
  const [orgMember, setOrgMember] = useState(p.orgMember);
  const active = p.active !== false;

  const save = async () => {
    setBusy(true);
    try {
      await updateAdminProfile(p.userId, { departments: depts, role, orgMember });
      enqueueSnackbar("Saved.", { variant: "success" });
      setEditing(false);
      onChanged();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Save failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await setProfileActive(p.userId, !active);
      enqueueSnackbar(active ? "User deactivated." : "User reactivated.", { variant: "info" });
      onChanged();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: "13px 16px", opacity: active ? 1 : 0.6 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box component="span" sx={{ fontSize: 15, fontWeight: 500, color: c.ink }}>
              {p.displayName || p.email || p.userId}
            </Box>
            {p.role === "admin" && <StatusPill tone="teal" label="admin" />}
            {!active && <StatusPill tone="danger" label="deactivated" />}
          </Box>
          <Box sx={{ fontSize: 12, color: c.muted2 }}>{p.email}</Box>
        </Box>
        <Box sx={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
            {p.orgMember && <ScopePillMui scope="company" />}
            {p.departments.map((d) => (
              <ScopePillMui key={d} scope={`dept:${d}`} />
            ))}
          </Box>
          {!isSelf && (
            <Button size="small" variant="outlined" startIcon={<Icon name="pencil" size={14} />} onClick={() => setEditing((e) => !e)}>
              Edit
            </Button>
          )}
          {!isSelf && (
            <Button
              size="small"
              variant={active ? "outlined" : "contained"}
              color={active ? "error" : "primary"}
              disabled={busy}
              startIcon={<Icon name={active ? "user-off" : "user-check"} size={14} />}
              onClick={() => void toggleActive()}
            >
              {active ? "Deactivate" : "Reactivate"}
            </Button>
          )}
        </Box>
      </Box>
      {editing && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${c.border}`, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box>
            <Box sx={{ fontSize: 12, color: c.muted2, mb: 0.5 }}>Departments</Box>
            <DeptChecks value={depts} onChange={setDepts} />
          </Box>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", fontSize: 12, color: c.muted2 }}>
              Role
              <Select size="small" value={role} onChange={(e) => setRole(e.target.value)} sx={{ fontSize: 13 }}>
                <MenuItem value="member">member</MenuItem>
                <MenuItem value="admin">admin</MenuItem>
              </Select>
            </Box>
            <FormControlLabel
              sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12, color: c.muted2 } }}
              control={<Checkbox size="small" checked={orgMember} onChange={(e) => setOrgMember(e.target.checked)} />}
              label="org member (company + crm access)"
            />
            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="small" variant="contained" disabled={busy} onClick={() => void save()}>Save</Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function AdminUsersPageMui() {
  const { profile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [depts, setDepts] = useState<string[]>([]);
  const [role, setRole] = useState("member");
  const [orgMember, setOrgMember] = useState(true);
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [p, i] = await Promise.allSettled([listAdminProfiles(), listInvites()]);
    if (p.status === "fulfilled") {
      setProfiles(p.value.sort((a, b) => (a.displayName || a.email || "").localeCompare(b.displayName || b.email || "")));
    }
    if (i.status === "fulfilled") setInvites(i.value);
    setLoading(false);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const sendInvite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    try {
      const r = await createInvite({ email: email.trim(), displayName: name.trim() || undefined, departments: depts, role, orgMember });
      enqueueSnackbar(
        r.provisioned
          ? `Added ${email.trim()} — they can sign in now with Aeronet SSO.`
          : `Invited ${email.trim()} — they'll be set up when they first sign in.`,
        { variant: "success" },
      );
      setEmail("");
      setName("");
      setDepts([]);
      setRole("member");
      setOrgMember(true);
      void load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Invite failed.", { variant: "error" });
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (e: string) => {
    try {
      await revokeInvite(e);
      enqueueSnackbar("Invite revoked.", { variant: "info" });
      void load();
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : "Failed.", { variant: "error" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: c.teal }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PageHeader
        title="Users"
        eyebrow={`${profiles.length} member${profiles.length === 1 ? "" : "s"} · ${invites.length} pending invite${invites.length === 1 ? "" : "s"}`}
        actions={
          <Button variant="outlined" size="small" startIcon={<Icon name="refresh" size={14} />} onClick={() => void load()}>
            Refresh
          </Button>
        }
      />

      <SectionCard>
        <SectionTitle icon="mail-plus">Add a teammate</SectionTitle>
        <Box sx={{ fontSize: 12, color: c.muted2, mt: 0.5, mb: 1.25 }}>
          Add by Aeronet email. If they already have an Aeronet account (everyone does), their profile is created immediately with the groups you set here and they sign in with Aeronet SSO — no extra steps.
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
          <TextField size="small" type="email" placeholder="name@aeronet.nz" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" sx={{ flex: 1, minWidth: 200 }} />
          <TextField size="small" placeholder="display name (optional)" value={name} onChange={(e) => setName(e.target.value)} aria-label="Display name" sx={{ flex: 1, minWidth: 200 }} />
        </Box>
        <Box sx={{ fontSize: 12, color: c.muted2, mb: 0.5 }}>Departments</Box>
        <Box sx={{ mb: 1.25 }}>
          <DeptChecks value={depts} onChange={setDepts} />
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", fontSize: 12, color: c.muted2 }}>
            Role
            <Select size="small" value={role} onChange={(e) => setRole(e.target.value)} sx={{ fontSize: 13 }}>
              <MenuItem value="member">member</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
            </Select>
          </Box>
          <FormControlLabel
            sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12, color: c.muted2 } }}
            control={<Checkbox size="small" checked={orgMember} onChange={(e) => setOrgMember(e.target.checked)} />}
            label="org member"
          />
          <Button
            variant="contained"
            startIcon={<Icon name="user-plus" size={14} />}
            disabled={inviting || !email.trim()}
            onClick={() => void sendInvite()}
            sx={{ ml: "auto" }}
          >
            Add user
          </Button>
        </Box>
      </SectionCard>

      {invites.length > 0 && (
        <SectionCard>
          <SectionTitle icon="clock">Pending invites</SectionTitle>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
            {invites.map((inv) => (
              <Box key={inv.email} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
                  <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.muted }}>
                    {inv.email}
                  </Box>
                  {inv.role === "admin" && <StatusPill tone="teal" label="admin" />}
                  {inv.orgMember && <ScopePillMui scope="company" />}
                  {inv.departments.map((d) => (
                    <ScopePillMui key={d} scope={`dept:${d}`} />
                  ))}
                </Box>
                <Button size="small" color="error" variant="outlined" startIcon={<Icon name="x" size={14} />} onClick={() => void revoke(inv.email)}>
                  Revoke
                </Button>
              </Box>
            ))}
          </Box>
        </SectionCard>
      )}

      {profiles.length === 0 ? (
        <EmptyState icon="users" title="No members yet">
          Invite your team above.
        </EmptyState>
      ) : (
        <Box sx={{ bgcolor: c.white, border: `1px solid ${c.border}`, borderRadius: `${brainTokens.radius.panel}px`, overflow: "hidden" }}>
          {profiles.map((p, i) => (
            <Box key={p.userId} sx={i > 0 ? { borderTop: `1px solid ${c.borderLight3}` } : undefined}>
              <UserRow p={p} isSelf={p.userId === profile?.userId} onChanged={() => void load()} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
